import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganizationMembers } from '@/hooks/useUsers';
import { useAuth } from '@/auth/auth0-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserRoleBadge } from '@/components/users/UserRoleBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Time } from '@/components/ui/time';
import { AlertCircle } from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { EmptyState } from '@/components/ui/empty-state';
import { Paginator } from '@/components/ui/paginator';

/**
 * Users Page
 *
 * Lists all members in the current user's organization.
 * Only visible to organization owners (delete:sites permission).
 */

const Users: React.FC = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading: queryLoading, error: queryError } = useOrganizationMembers({ page });
  const members = data?.items ?? [];
  const pagination = data?.pagination;
  const { isAuthenticated, isSdkReady } = useAuth();
  // Mirror the cold-load pattern: treat the auth-init window as loading so
  // the empty-state doesn't flash before the query is enabled.
  const isLoading = queryLoading || !isAuthenticated || !isSdkReady;
  const error = queryError instanceof Error ? queryError.message : null;

  // Get initials for avatar fallback
  const getInitials = (email: string, name?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-6">
        <Breadcrumbs />
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Users</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Manage your organization members
          </p>
        </div>

        {error ? (
          <EmptyState
            variant="error"
            icon={AlertCircle}
            title="Failed to load users"
            description={error}
          />
        ) : (
          <div className="rounded bg-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeletons
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  </TableRow>
                ))
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                    No members found
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Link
                        to={`/users/${encodeURIComponent(member.auth0Id)}`}
                        className="flex items-center gap-3 hover:opacity-80"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.picture} alt={member.email} />
                          <AvatarFallback>
                            {getInitials(member.email, member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">
                            {member.name || member.email.split('@')[0]}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {member.email}
                          </p>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <UserRoleBadge role={member.primaryRole} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <Time value={member.createdAt} format="short" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Paginator
            pagination={pagination}
            onPageChange={setPage}
            className="mt-6"
          />
        </div>
        )}
      </div>
    </div>
  );
};

export default Users;
